import type { Order, Recurrence } from '@wix/ambassador-pricing-plans-v2-order/types'
import { CancellationCause } from '@wix/ambassador-pricing-plans-v2-order/types'

export function mapV2OrderToVeloOrder(order: Order) {
	const subscription = getSubscription(order)
	const cancellation = mapV2CancellationToVelo(order.cancellation?.cause)
	const period = getPeriod(order, subscription)
	const priceAmount = getPriceAmount(order)

	return {
		id: order.id,
		planId: order.planId,
		memberId: order.buyer?.memberId,
		roleId: order.planId,
		orderType: order.type,
		status: order.status,
		wixPayOrderId: order.wixPayOrderId ?? undefined,
		paymentStatus: order.lastPaymentStatus,
		price: {
			amount: Number(priceAmount),
			currency: order.priceDetails?.currency ?? firstPrice(order)?.currency,
		},
		planName: order.planName,
		planDescription: order.planDescription,
		recurring: !!subscription,
		freeTrialDays: order.freeTrialDays ?? order.priceDetails?.freeTrialDays ?? undefined,
		validFor: {
			forever: isForever(order, subscription),
			period: period.amount ? { amount: period.amount, unit: period.unit } : {},
		},
		validFrom: toVeloDate(order.startDate),
		validUntil: toVeloDate(order.endDate),
		dateCreated: toVeloDate(order.createdDate),
		cancellationReason: cancellation.reason,
		cancellationInitiator: cancellation.initiator,
	}
}

function mapV2CancellationToVelo(cause?: CancellationCause) {
	switch (cause) {
		case CancellationCause.OWNER_ACTION:
			return { reason: 'OWNER_CANCELED', initiator: 'OWNER' }
		case CancellationCause.MEMBER_ACTION:
			return { reason: 'MEMBER_CANCELED', initiator: 'MEMBER' }
		case CancellationCause.PAYMENT_FAILURE:
			return { reason: 'PAYMENT_FAILED', initiator: 'PAYMENT_FAILURE' }
		case CancellationCause.PAYMENT_SETUP_FAILURE:
			return { reason: 'PAYMENT_PROVIDER_CANCELED', initiator: 'SETUP_FAILURE' }
		default:
			return { reason: 'CANCELLATION_REASON_UNDEFINED', initiator: 'UNDEFINED' }
	}
}

const toVeloDate = (value?: Date | string | number | null) =>
	value ? (value instanceof Date ? value : new Date(value)) : undefined

const getSubscription = (order: Order): Recurrence | undefined =>
	order.pricing?.subscription ?? order.priceDetails?.subscription

const firstPrice = (order: Order) => order.pricing?.prices?.[0]?.price

const getPriceAmount = (order: Order) =>
	order.priceDetails?.planPrice ??
	order.planPrice ??
	firstPrice(order)?.subtotal ??
	order.priceDetails?.subtotal ??
	firstPrice(order)?.total ??
	order.priceDetails?.total

const getPeriod = (order: Order, subscription: Recurrence | undefined) => {
	if (subscription) {
		const cycleCount = subscription.cycleCount ?? 0
		const cycleDuration = subscription.cycleDuration
		if (cycleCount > 0 && cycleDuration?.unit) {
			return { amount: (cycleDuration.count ?? 1) * cycleCount, unit: cycleDuration.unit }
		}
		return {}
	}

	const singlePaymentForDuration =
		order.pricing?.singlePaymentForDuration ?? order.priceDetails?.singlePaymentForDuration
	if (singlePaymentForDuration) {
		return { amount: singlePaymentForDuration.count, unit: singlePaymentForDuration.unit }
	}

	return {}
}

const isForever = (order: Order, subscription: Recurrence | undefined) =>
	order.pricing?.singlePaymentUnlimited === true ||
	order.priceDetails?.singlePaymentUnlimited === true ||
	subscription?.cycleCount === 0
